import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { House, ShoppingBag, Stethoscope, ShoppingCart, User } from "lucide-react";
import "./styles/MobileBottomNav.css";
import "../styles/global.css";

/* ═══════════════════════════════════════════════════════════════
   MobileBottomNav.jsx
   ✅ Zero external utility imports
   ✅ Cart count syncs automatically when any page calls:
      window.dispatchEvent(new CustomEvent("cart:updated", { detail: { count: N } }))
   ✅ Reads localStorage on first load so badge shows immediately
   ✅ Active tab detection per route
═══════════════════════════════════════════════════════════════ */

/* ─── styles ─────────────────────────────────────────────────── */
/* ─── which routes activate each tab ──────────────────────────── */
const TABS = [
  {
    key: "home",
    label: "Home",
    to: "/#",
    match: ["/home", "/"],
    Icon: ({ active }) => (
      <House size={22} strokeWidth={2} color={active ? "#fff" : "#79716B"} />
    ),
  },
  {
    key: "medicine",
    label: "Medicine",
    to: "/ayush-product-list",
    match: ["/ayush-product-list", "/product-list", "/product", "/category"],
    Icon: ({ active }) => (
      <ShoppingBag size={22} strokeWidth={2} color={active ? "#fff" : "#79716B"} />
    ),
  },
  {
    key: "consult",
    label: "Consult",
    to: "/doctor-list-page",
    match: ["/doctor-list-page", "/doctor", "/clinic-list-page"],
    Icon: ({ active }) => (
      <Stethoscope size={22} strokeWidth={2} color={active ? "#fff" : "#79716B"} />
    ),
  },
  {
    key: "cart",
    label: "Cart",
    to: "/cart",
    match: ["/cart"],
    isCart: true,
    Icon: ({ active }) => (
      <ShoppingCart size={22} strokeWidth={2} color={active ? "#fff" : "#79716B"} />
    ),
  },
  {
    key: "profile",
    label: "Profile",
    to: null,           // resolved from userId prop
    match: ["/myprofile", "/login-register"],
    Icon: ({ active }) => (
      <User size={22} strokeWidth={2} color={active ? "#fff" : "#79716B"} />
    ),
  },
];

/* ─── is this tab active? ─────────────────────────────────────── */
const tabActive = (tab, pathname) =>
  tab.match.some(
    (p) => pathname === p || pathname.startsWith(p + "/") || pathname.startsWith(p + "?")
  );

/* ═══════════════════════════════════════════════════════════════
   COMPONENT
   Props:
     userId  — string | null   (from localStorage or auth state)
═══════════════════════════════════════════════════════════════ */
const MobileBottomNav = ({ userId = null }) => {
  const { pathname } = useLocation();

  /* ── cart count — self-contained inside this file ──
     Seeds from localStorage so badge shows on first render.
     Listens for the "cart:updated" event fired by addToCart()
     anywhere in the app.                                      */
  const [cartCount, setCartCount] = useState(() => {
    const saved = localStorage.getItem("cart_count");
    return saved ? Number(saved) : 0;
  });

  useEffect(() => {
    const handler = (e) => {
      const n = Number(e?.detail?.count ?? 0);
      setCartCount(n);
      localStorage.setItem("cart_count", String(n));
    };
    window.addEventListener("cart:updated", handler);
    return () => window.removeEventListener("cart:updated", handler);
  }, []);

  return (
    <>
      {/* <style>{STYLES}</style> */}

      <nav className="mnav-bar cmo-sticky-bar cmo-sticky-bar-1">
        {TABS.map((tab) => {
          const active = tabActive(tab, pathname);
          const to = tab.key === "profile"
            ? (userId ? "/myprofile" : "/login-register")
            : tab.to;

          return (
            <Link
              key={tab.key}
              to={to}
              className={`mnav-tab mobile-menu-bar${active ? " mnav-active is-active" : ""}`}
            >
              {/* icon bubble */}
              <div className={`mnav-bubble mobile-menu-bg${active ? " active" : ""}`}>
                <tab.Icon active={active} />
              </div>

              {/* cart badge — updates live whenever addToCart fires the event */}
              {tab.isCart && cartCount > 0 && (
                <span className="mnav-badge cartcount">
                  {cartCount > 99 ? "99+" : cartCount}
                </span>
              )}

              <span className={`mnav-label${active ? " active-2" : ""}`}>
                {tab.label}
              </span>
            </Link>
          );
        })}
      </nav>
    </>
  );
};

export default MobileBottomNav;


/* ═══════════════════════════════════════════════════════════════
   HOW TO FIRE THE CART UPDATE from any page (ProductList, etc.)
   ───────────────────────────────────────────────────────────────
   In your addToCart() success callback, replace the old DOM hack:

     // OLD (remove this):
     const el = document.querySelector(".cartcount");
     if (el) el.innerText = count;

     // NEW (add this one line):
     window.dispatchEvent(
       new CustomEvent("cart:updated", { detail: { count: count } })
     );

   That's it — no imports needed in ProductList.
   The event updates the badge in MobileBottomNav automatically.
═══════════════════════════════════════════════════════════════ */
